import { inject, provide, shallowRef, type InjectionKey } from 'vue';
import { EQuickEntryKind } from './quickEntry.enums';

export type TQuickEntryFocusTarget =
  | { kind: EQuickEntryKind.Note }
  | { kind: EQuickEntryKind.PerformanceAssessment; performanceAssessmentId: number }
  | { kind: EQuickEntryKind.SupportMeasure };

export interface IInlineQuickEntryComposerHandle {
  focus: (target?: TQuickEntryFocusTarget) => Promise<void>;
}

export interface IInlineQuickEntryComposerRegistry {
  focus: (target?: TQuickEntryFocusTarget) => Promise<boolean>;
  register: (composer: IInlineQuickEntryComposerHandle) => void;
  unregister: (composer: IInlineQuickEntryComposerHandle) => void;
}

export const isQuickEntryFocusTargetSelected = (
  draft: { kind: EQuickEntryKind; performanceAssessmentId: number | null },
  target: TQuickEntryFocusTarget,
): boolean => {
  if (target.kind === EQuickEntryKind.Note) return draft.kind === EQuickEntryKind.Note;
  if (target.kind === EQuickEntryKind.SupportMeasure) return draft.kind === EQuickEntryKind.SupportMeasure;
  return (
    draft.kind === EQuickEntryKind.PerformanceAssessment &&
    draft.performanceAssessmentId === target.performanceAssessmentId
  );
};

export const createInlineQuickEntryComposerRegistry = (): IInlineQuickEntryComposerRegistry => {
  const activeComposer = shallowRef<IInlineQuickEntryComposerHandle | null>(null);

  return {
    focus: async (target) => {
      const composer = activeComposer.value;
      if (!composer) return false;
      await composer.focus(target);
      return true;
    },
    register: (composer) => {
      activeComposer.value = composer;
    },
    unregister: (composer) => {
      if (activeComposer.value === composer) activeComposer.value = null;
    },
  };
};

const InlineQuickEntryComposerRegistryKey: InjectionKey<IInlineQuickEntryComposerRegistry> = Symbol(
  'inlineQuickEntryComposerRegistry',
);

export const provideInlineQuickEntryComposerRegistry = (): IInlineQuickEntryComposerRegistry => {
  const registry = createInlineQuickEntryComposerRegistry();
  provide(InlineQuickEntryComposerRegistryKey, registry);
  return registry;
};

export const useInlineQuickEntryComposerRegistry = (): IInlineQuickEntryComposerRegistry | null =>
  inject(InlineQuickEntryComposerRegistryKey, null);
